/**
 * IntegrationHubPanel.jsx
 * ========================
 * External system integration connectors hub.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { 
  Plug, RefreshCw, Settings, CheckCircle2, XCircle,
  Loader2, Cloud, Database, Webhook, ExternalLink,
  ShieldCheck, AlertTriangle, Building2, Bot
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONNECTOR_ICONS = {
  quickbooks: Database,
  hubspot: Cloud,
  webhook: Webhook,
  salesforce: Cloud,
  zoho: Cloud,
  tally: Database,
  sap: Building2,
  uipath: Bot,
  blueprism: Bot,
  automationanywhere: Bot,
  default: Plug
};

const CONNECTOR_COLORS = {
  quickbooks: 'bg-green-100 text-green-700',
  hubspot: 'bg-orange-100 text-orange-700',
  webhook: 'bg-blue-100 text-blue-700',
  salesforce: 'bg-sky-100 text-sky-700',
  zoho: 'bg-red-100 text-red-700',
  tally: 'bg-amber-100 text-amber-700',
  sap: 'bg-indigo-100 text-indigo-700',
  uipath: 'bg-orange-100 text-orange-700',
  blueprism: 'bg-cyan-100 text-cyan-700',
  automationanywhere: 'bg-rose-100 text-rose-700'
};

const IntegrationHubPanel = () => {
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configuring, setConfiguring] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [configForm, setConfigForm] = useState({});
  const [expandedConnector, setExpandedConnector] = useState(null);

  const updateField = (type, field, value) => setConfigForm(prev => ({
    ...prev,
    [type]: { ...prev[type], [field]: value }
  }));

  useEffect(() => {
    fetchConnectors();
  }, []);

  const fetchConnectors = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('reasondms_token');
      const response = await fetch(`${API_URL}/api/agentic-extensions/integrations/connectors`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConnectors(data.connectors || []);
      }
    } catch (error) {
      console.error('Failed to fetch connectors:', error);
    } finally {
      setLoading(false);
    }
  };

  const configureConnector = async (connectorType) => {
    setConfiguring(connectorType);
    try {
      const token = localStorage.getItem('reasondms_token');
      const config = configForm[connectorType] || {};
      
      const response = await fetch(`${API_URL}/api/agentic-extensions/integrations/configure`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          connector_type: connectorType,
          config: config,
          enabled: true
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || data.error || 'Configuration failed');
      }
      
      toast.success(`${connectorType} configured successfully`);
      setExpandedConnector(null);
      fetchConnectors();
    } catch (error) {
      console.error('Configure error:', error);
      toast.error(error.message || 'Failed to configure connector');
    } finally {
      setConfiguring(null);
    }
  };

  const triggerSync = async (connectorType) => {
    setSyncing(connectorType);
    try {
      const token = localStorage.getItem('reasondms_token');
      const response = await fetch(
        `${API_URL}/api/agentic-extensions/integrations/${connectorType}/sync`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error('Sync failed');
      
      const data = await response.json();
      toast.success(data.message || 'Sync triggered successfully');
    } catch (error) {
      console.error('Sync error:', error);
      toast.error('Failed to trigger sync');
    } finally {
      setSyncing(null);
    }
  };

  const getConnectorIcon = (type) => {
    return CONNECTOR_ICONS[type] || CONNECTOR_ICONS.default;
  };

  const getConnectorColor = (type) => {
    return CONNECTOR_COLORS[type] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="integration-hub-panel">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Plug className="h-5 w-5 text-primary" />
            <CardTitle>Integration Hub</CardTitle>
          </div>
          <Button variant="outline" size="sm" onClick={fetchConnectors}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </div>
        <CardDescription>
          Connect to external systems and services
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Notice Banner */}
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-amber-800">ERP, CRM &amp; RPA Connectors</h4>
            <p className="text-sm text-amber-700 mt-1">
              Configure connectors with your credentials. SAP S/4HANA runs a live OData sync; RPA connectors (UiPath, Blue Prism, Automation Anywhere) dispatch automation jobs once Orchestrator/Control Room credentials are provided.
            </p>
          </div>
        </div>

        {/* Connectors List */}
        <div className="space-y-4">
          {connectors.map(connector => {
            const Icon = getConnectorIcon(connector.type);
            const colorClass = getConnectorColor(connector.type);
            
            return (
              <div
                key={connector.type}
                className="border rounded-lg p-4 hover:border-primary/50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${colorClass}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="font-medium flex items-center gap-2">
                        {connector.name}
                        {connector.configured ? (
                          <Badge variant="success" className="bg-green-100 text-green-700">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Connected
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Not Configured</Badge>
                        )}
                      </h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        {connector.description}
                      </p>
                      
                      {/* Features */}
                      {connector.features && (
                        <div className="flex gap-2 mt-2 flex-wrap">
                          {connector.features.map((feature, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {feature}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {connector.configured && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => triggerSync(connector.type)}
                        disabled={syncing === connector.type}
                      >
                        {syncing === connector.type ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Sync
                          </>
                        )}
                      </Button>
                    )}
                    <Button
                      variant={connector.configured ? "outline" : "default"}
                      size="sm"
                      data-testid={`configure-${connector.type}`}
                      onClick={() => setExpandedConnector(expandedConnector === connector.type ? null : connector.type)}
                    >
                      <Settings className="h-4 w-4 mr-1" />
                      {connector.configured ? 'Settings' : 'Configure'}
                    </Button>
                  </div>
                </div>

                {/* Configuration Panel (driven by required_config) */}
                {expandedConnector === connector.type && (
                  <div className="mt-4 pt-4 border-t" data-testid={`config-panel-${connector.type}`}>
                    <h5 className="text-sm font-medium mb-3">Configuration</h5>
                    <div className="grid grid-cols-2 gap-3">
                      {(connector.required_config || []).map((field) => (
                        <div key={field}>
                          <label className="text-xs text-muted-foreground block mb-1 capitalize">
                            {field.replace(/_/g, ' ')}<span className="text-red-500">*</span>
                          </label>
                          {field === 'auth_type' ? (
                            <select
                              data-testid={`config-${connector.type}-${field}`}
                              value={configForm[connector.type]?.[field] || 'basic'}
                              onChange={(e) => updateField(connector.type, field, e.target.value)}
                              className="h-8 text-sm w-full border rounded-md px-2 bg-white"
                            >
                              <option value="basic">Basic Auth</option>
                              <option value="oauth">OAuth2</option>
                            </select>
                          ) : (
                            <Input
                              data-testid={`config-${connector.type}-${field}`}
                              type={/secret|password|key|token/i.test(field) ? 'password' : 'text'}
                              value={configForm[connector.type]?.[field] || ''}
                              onChange={(e) => updateField(connector.type, field, e.target.value)}
                              className="h-8 text-sm"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-end mt-3">
                      <Button
                        size="sm"
                        data-testid={`save-config-${connector.type}`}
                        onClick={() => configureConnector(connector.type)}
                        disabled={configuring === connector.type}
                      >
                        {configuring === connector.type ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        ) : (
                          <ShieldCheck className="h-4 w-4 mr-1" />
                        )}
                        Save &amp; Connect
                      </Button>
                    </div>
                  </div>
                )}

                {/* Last Sync Info */}
                {connector.last_sync && (
                  <div className="mt-3 pt-3 border-t flex items-center justify-between text-xs text-muted-foreground">
                    <span>Last synced: {new Date(connector.last_sync).toLocaleString()}</span>
                    <span className="flex items-center gap-1">
                      <ShieldCheck className="h-3 w-3 text-green-500" />
                      {connector.sync_status || 'Success'}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {connectors.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <Plug className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No connectors available</p>
          </div>
        )}

        {/* Add Custom Webhook */}
        <div className="border-t pt-4">
          <h4 className="font-medium mb-3 flex items-center gap-2">
            <Webhook className="h-4 w-4 text-primary" />
            Custom Webhook
          </h4>
          <div className="flex gap-2">
            <Input
              placeholder="Enter webhook URL..."
              value={configForm.custom_webhook?.url || ''}
              onChange={(e) => setConfigForm(prev => ({
                ...prev,
                custom_webhook: { ...prev.custom_webhook, url: e.target.value }
              }))}
              className="flex-1"
            />
            <Button
              variant="outline"
              onClick={() => configureConnector('webhook')}
              disabled={configuring === 'webhook'}
            >
              {configuring === 'webhook' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Add Webhook'
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Receive document events via HTTP POST to your endpoint
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default IntegrationHubPanel;
